import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ProfileDraftComparison } from "@/components/athlete/ProfileDraftComparison";
import { isReviewPilot } from "@/lib/featureFlags";

type Review = {
  draft_id: string; draft_hash: string; proposed: Record<string, unknown>; live: Record<string, unknown>;
  live_changed_since_draft: boolean; approval_valid: boolean; changes_requested: string | null;
};
const err = (e: any) => e?.message ?? "Something went wrong";
const card = "rounded-2xl border border-border bg-card p-5";

/**
 * Pilot-only review controls for an athlete whose profile is already live.
 * Two distinct cases: review SIIS's proposed draft, or approve the current live profile as-is.
 * Renders nothing for athletes outside the pilot allowlist.
 */
const PilotProfileReview = ({ athleteId, slug, published, approvedCurrent, onChanged }: {
  athleteId: string; slug: string; published: boolean; approvedCurrent: boolean; onChanged: () => void;
}) => {
  const allowed = isReviewPilot(athleteId) && published;
  const [review, setReview] = useState<Review | null | undefined>(undefined);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data, error } = await supabase.rpc("profile_draft_review", { _athlete_id: athleteId });
    if (error) { toast.error(err(error)); setReview(null); return; }
    setReview((data as unknown as Review) ?? null);
  };
  useEffect(() => { if (allowed) load(); }, [allowed, athleteId]);
  if (!allowed || review === undefined) return null;

  const reviewDraft = async (ok: boolean) => {
    if (!review) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("athlete_review_profile_draft", {
        _draft_id: review.draft_id, _expected_hash: review.draft_hash, _approve: ok, _note: ok ? null as any : note,
      });
      if (error) throw error;
      toast.success(ok ? "Thanks — you approved this exact version. SIIS will launch it." : "Sent to SIIS.");
      setNote(""); await load(); onChanged();
    } catch (e) { toast.error(err(e)); await load(); } finally { setBusy(false); }
  };

  const approveLive = async () => {
    setBusy(true);
    try {
      const h = await supabase.rpc("athlete_my_profile_hash");
      if (h.error) throw h.error;
      const { error } = await supabase.rpc("athlete_approve_published_profile", { _expected_hash: h.data as string });
      if (error) throw error;
      toast.success("Thanks — you approved your current live profile.");
      onChanged();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  if (review) {
    return (
      <div className="space-y-3">
        <ProfileDraftComparison live={review.live} proposed={review.proposed}
          liveChangedSinceDraft={review.live_changed_since_draft} approvalValid={review.approval_valid} />
        {!review.approval_valid && !review.live_changed_since_draft && (
          <div className={card}>
            <textarea aria-label="What should change?" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3}
              placeholder="What should change? (only needed if requesting changes)" className="w-full rounded-lg border border-input bg-background p-2 text-sm" />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button disabled={busy} onClick={() => reviewDraft(true)}>Approve these changes</Button>
              <Button variant="outline" disabled={busy || !note.trim()} onClick={() => reviewDraft(false)}>Request changes</Button>
            </div>
            {review.changes_requested && <p className="mt-2 text-xs text-muted-foreground">Your last note: “{review.changes_requested}”</p>}
          </div>
        )}
      </div>
    );
  }

  if (approvedCurrent) return null;
  return (
    <section className={card} aria-labelledby="approve-live-h">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">Approve current live profile</p>
      <h2 id="approve-live-h" className="font-display text-xl mt-1">Happy with what's already public?</h2>
      <p className="mt-1 text-sm text-muted-foreground">Approving records this exact live version. Nothing on your profile changes, and it stays public.</p>
      <a href={`/athlete/${slug}`} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-primary-strong">View my live profile ↗</a>
      <div className="mt-3"><Button disabled={busy} onClick={approveLive}>Approve my live profile</Button></div>
    </section>
  );
};

export default PilotProfileReview;
